import type { JSONContent } from "@tiptap/core";

/** Matches persisted mentions: `#[label](id)` (current) or `@[label](id)` (legacy). */
const MENTION_REGEX = /(?:@|#)\[([^\]]+)\]\(([^)]+)\)/g;
const PASTED_REGEX = /~~pasted~~\[([^\]]+)\]\(([^)]+)\)/g;

/**
 * Serialize Tiptap JSON content to the markup string format the backend expects.
 * Mention nodes become `#[label](id)`, paragraphs are joined with newlines.
 */
export function serializeTiptapToMarkup(doc: JSONContent): string {
  if (!doc.content) return "";

  return doc.content
    .map((block) => {
      if (block.type === "paragraph") {
        return serializeInlineContent(block.content || []);
      }
      return "";
    })
    .join("\n");
}

function serializeInlineContent(nodes: JSONContent[]): string {
  return nodes
    .map((node) => {
      if (node.type === "mention") {
        const baseLabel = String(node.attrs?.label || "");
        const id = node.attrs?.id || "";
        const bookTitle = typeof node.attrs?.bookTitle === "string" ? node.attrs.bookTitle.trim() : "";
        const label = bookTitle && !baseLabel.endsWith(` (${bookTitle})`) ? `${baseLabel} (${bookTitle})` : baseLabel;
        return `#[${label}](${id})`;
      }
      if (node.type === "pastedContent") {
        const wordCount = node.attrs?.wordCount || "0";
        const id = node.attrs?.id || "";
        return `~~pasted~~[${wordCount}](${id})`;
      }
      if (node.type === "hardBreak") {
        return "\n";
      }
      if (node.type === "text") {
        return node.text || "";
      }
      return "";
    })
    .join("");
}

/**
 * Parse a markup string into Tiptap JSON content.
 * `#[label](id)` or legacy `@[label](id)` patterns become mention nodes, everything else is text.
 */
export function parseMarkupToTiptap(markup: string): JSONContent {
  if (!markup) {
    return { type: "doc", content: [{ type: "paragraph" }] };
  }

  const lines = markup.split("\n");
  const paragraphs: JSONContent[] = lines.map((line) => {
    const content = parseLineToInlineContent(line);
    return { type: "paragraph", content: content.length > 0 ? content : undefined };
  });

  return { type: "doc", content: paragraphs };
}

// Combined: mentions (`#[]()` or legacy `@[]()`) and ~~pasted~~ markers
const COMBINED_REGEX = /(?:@|#)\[([^\]]+)\]\(([^)]+)\)|~~pasted~~\[([^\]]+)\]\(([^)]+)\)/g;

function splitSeriesDocumentLabel(rawLabel: string): { label: string; bookTitle?: string } {
  if (!rawLabel.endsWith(")")) return { label: rawLabel };

  let depth = 0;
  let openIndex = -1;
  for (let i = rawLabel.length - 1; i >= 0; i--) {
    const ch = rawLabel[i];
    if (ch === ")") {
      depth += 1;
    } else if (ch === "(") {
      depth -= 1;
      if (depth === 0) {
        openIndex = i;
        break;
      }
      if (depth < 0) {
        return { label: rawLabel };
      }
    }
  }

  if (openIndex <= 0 || depth !== 0) {
    return { label: rawLabel };
  }

  // Only treat trailing "(...)" as book title when separated from label by whitespace.
  if (!/\s$/.test(rawLabel.slice(0, openIndex))) {
    return { label: rawLabel };
  }

  const bookTitle = rawLabel.slice(openIndex + 1, -1).trim();
  const label = rawLabel.slice(0, openIndex).trimEnd();
  if (!bookTitle || !label) {
    return { label: rawLabel };
  }
  return { label, bookTitle };
}

function parseLineToInlineContent(line: string): JSONContent[] {
  const nodes: JSONContent[] = [];
  const regex = new RegExp(COMBINED_REGEX.source, "g");
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(line)) !== null) {
    if (match.index > lastIndex) {
      nodes.push({ type: "text", text: line.substring(lastIndex, match.index) });
    }

    if (match[1] !== undefined && match[2] !== undefined) {
      const id = match[2];
      const rawLabel = match[1];
      const { label, bookTitle } =
        typeof id === "string" && id.startsWith("series_document::")
          ? splitSeriesDocumentLabel(rawLabel)
          : { label: rawLabel, bookTitle: undefined as string | undefined };
      nodes.push({
        type: "mention",
        attrs: { id, label, ...(bookTitle ? { bookTitle } : {}) },
      });
    } else if (match[3] !== undefined && match[4] !== undefined) {
      nodes.push({
        type: "pastedContent",
        attrs: { id: match[4], wordCount: match[3] },
      });
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < line.length) {
    nodes.push({ type: "text", text: line.substring(lastIndex) });
  }

  return nodes;
}

/**
 * Get plain text from markup (for display purposes like character counting).
 */
export function getPlainTextFromMarkup(markup: string): string {
  return markup
    .replace(MENTION_REGEX, "#$1")
    .replace(PASTED_REGEX, "Pasted $1 words");
}
